import type { BootstrapResponse, randomnessHealthSchema } from "./schemas";
import { isElysium, elysiumWriteBlock, elysiumMintStateBlock, elysiumOwnerRedeemEnabled, elysiumBootstrapReasons } from "../config/elysium";

export type ActionName = "mining" | "mint" | "finalize" | "redeem";
export type ActionDecision = { state: "ALLOWED" | "BLOCKED" | "UNKNOWN"; reasons: string[] };

/** FE facts, not a proposed backend response schema. */
export interface ActionPolicyInput {
  permissions: Record<ActionName, boolean | null>;
  writes: boolean | null;
  execution: boolean | null;
  redeemExecution?: boolean;
  deployment: boolean;
  readiness: ActionDecision;
}

export function isIsolatedLocalExecution(bootstrap: BootstrapResponse | null): boolean {
  return Boolean(bootstrap && bootstrap.environment !== "production" &&
    bootstrap.capabilities.mintGatewayMode === "LOCAL_EVM_TEST_ONLY" &&
    bootstrap.capabilities.localTestOnlyMintTransactionEnabled &&
    bootstrap.deployment.projectContractsStatus === "LOCAL_TEST_ONLY");
}

/** Existing bootstrap write flags admit owner redemption independently of new mint readiness. */
export function isProductionRedeemExecution(bootstrap: BootstrapResponse | null): boolean {
  return Boolean(bootstrap && bootstrap.environment === "production" && !bootstrap.forkTestOnly &&
    bootstrap.capabilities.mainnetWritesEnabled && bootstrap.deployment.writesEnabled &&
    bootstrap.capabilities.mintGatewayMode === "MAINNET_READ_ONLY" &&
    bootstrap.deployment.projectContractsStatus === "VERIFIED_WRITE_ENABLED" &&
    bootstrap.deployment.sourceTokenStatus === "REGISTERED" &&
    bootstrap.contracts.collectionAddress && bootstrap.contracts.rewardVaultAddress && bootstrap.contracts.mintChainId &&
    bootstrap.contracts.mintChainId === bootstrap.deployment.executionChainId);
}

/** Only map fields published by the current BE. Unknown future keys grant nothing. */
export function currentActionFacts(bootstrap: BootstrapResponse | null,
  health: ReturnType<typeof randomnessHealthSchema.parse> | null = null,
  mintState: BootstrapResponse["mintState"] | null = bootstrap?.mintState ?? null): ActionPolicyInput {
  if (!bootstrap) return { permissions: { mining: null, mint: null, finalize: null, redeem: null },
    writes: null, execution: null, deployment: false,
    readiness: { state: "UNKNOWN", reasons: ["RANDOMNESS_HEALTH_UNAVAILABLE"] } };
  const v3Block = elysiumWriteBlock(bootstrap);
  if (isElysium(bootstrap)) {
    const redeem = elysiumOwnerRedeemEnabled(bootstrap);
    const identityValid = elysiumBootstrapReasons(bootstrap).length === 0;
    const readiness = elysiumMintStateBlock(bootstrap, mintState);
    return { permissions: { mining: identityValid && bootstrap.capabilities.miningEnabled,
      mint: bootstrap.capabilities.publicMintEnabled, finalize: false, redeem },
      writes: bootstrap.capabilities.mainnetWritesEnabled && bootstrap.deployment.writesEnabled,
      execution: !v3Block, redeemExecution: redeem, deployment: identityValid,
      readiness: readiness ? { state: "BLOCKED", reasons: [readiness] } : { state: "ALLOWED", reasons: [] } };
  }
  const local = isIsolatedLocalExecution(bootstrap);
  const onPrepare = bootstrap.mintState.providerCheckMode === "ON_PREPARE" || health?.providerCheckMode === "ON_PREPARE";
  const deployment = Boolean(bootstrap.contracts.collectionAddress && bootstrap.contracts.mintChainId &&
    bootstrap.contracts.mintChainId === bootstrap.deployment.executionChainId);
  const blockers = health ? [...health.mirror.freshness.reasons,
    ...(health.providerReadiness?.blockingReasons ?? []),
    ...(!health.mirror.freshness.current ? ["NFT_INDEXER_NOT_CURRENT"] : []),
    ...(!onPrepare && (!bootstrap.capabilities.randomnessProviderApproved || health.providerApproval !== "APPROVED") ?
      ["PROVIDER_PENDING"] : [])] : [];
  // The existing isolated path uses contract simulation and test-only preparation.
  // It does not require production provider approval or enable production sends.
  const readiness: ActionDecision = local ? { state: "ALLOWED", reasons: [] } :
    blockers.length ? { state: "BLOCKED", reasons: [...new Set(blockers)] } :
      { state: "UNKNOWN", reasons: [onPrepare ? "MINT_PREPARATION_REQUIRED" : "RANDOMNESS_READINESS_UNCONFIRMED"] };
  return {
    permissions: { mining: bootstrap.capabilities.miningEnabled,
      mint: local || bootstrap.capabilities.publicMintEnabled, finalize: null,
      // Redeem uses the existing global write/deployment flags; finalize remains unchanged.
      redeem: local || isProductionRedeemExecution(bootstrap) ? true : null },
    writes: local || bootstrap.capabilities.mainnetWritesEnabled && bootstrap.deployment.writesEnabled,
    execution: local || onPrepare && bootstrap.environment === "production" && !bootstrap.forkTestOnly &&
      bootstrap.capabilities.mintGatewayMode === "MAINNET_READ_ONLY" &&
      bootstrap.deployment.projectContractsStatus === "VERIFIED_WRITE_ENABLED", deployment, readiness,
    redeemExecution: local || isProductionRedeemExecution(bootstrap),
  };
}
